import React, { useState, useCallback, useEffect } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import Grid, { GridColProps } from '../../components/Grid/Grid';
import { useCheckinLogs } from '@/features/checkin/hooks';
import { getCheckinMemberDisplayStatus } from '@/features/checkin/helpers';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';

const PAGE_LIMIT = 30;

const CheckinHistoryPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;
    const navigate = useNavigate();

    // 필터 상태
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    // 디바운스 검색
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data: checkinData, isLoading } = useCheckinLogs({
        centerId,
        startDate,
        endDate,
        search: debouncedSearch || undefined,
        page,
        limit: PAGE_LIMIT,
    });

    const checkins = checkinData?.results ?? [];
    const totalResults = checkinData?.totalResults ?? 0;
    const totalPages = checkinData?.totalPages ?? 1;

    const colDefs: GridColProps[] = [
        { field: 'memberName', headerName: '회원명', width: 120, align: 'center' },
        { field: 'phoneNumber', headerName: '연락처', width: 150, align: 'center', customFormat: (value) => formatKoreanPhoneNumber(value, '-') },
        {
            field: 'memberType',
            headerName: '회원타입',
            width: 120,
            align: 'center',
            customComponent: (_value, row) => {
                const status = getCheckinMemberDisplayStatus(row);
                if (status === 'public') {
                    return <span style={{ fontSize: 13, color: '#2563EB', fontWeight: 500 }}>퍼블릭 회원</span>;
                }
                if (status === 'enrolled') {
                    return <span style={{ fontSize: 13, color: '#047857', fontWeight: 500 }}>수강 회원</span>;
                }
                if (status === 'visitor') {
                    return (
                        <span
                            style={{
                                padding: '3px 10px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                border: '1px solid #BFDBFE',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            방문객
                        </span>
                    );
                }
                return (
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: '#FEF3C7',
                            color: '#B45309',
                            border: '1px solid #FDE68A',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        이용권 없음
                    </span>
                );
            },
        },
        {
            field: 'checkinAt',
            headerName: '체크인 시간',
            type: 'date',
            dateFormat: 'YYYY-MM-DD HH:mm',
            width: 180,
            align: 'center',
            isSort: true,
        },
        {
            field: 'checkoutAt',
            headerName: '체크아웃 시간',
            type: 'date',
            dateFormat: 'YYYY-MM-DD HH:mm',
            width: 180,
            align: 'center',
            nullValue: '-',
        },
        {
            field: 'method',
            headerName: '방법',
            width: 100,
            align: 'center',
            customFormat: (value) => {
                const labels: Record<string, string> = { kiosk_phone: '키오스크', qr: 'QR', manual: '수동' };
                return labels[value] || value;
            },
        },
    ];

    const datePresets = [
        { label: '오늘', start: dayjs().format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '이번주', start: dayjs().startOf('week').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '이번달', start: dayjs().startOf('month').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '지난달', start: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') },
    ];

    const inputStyle: React.CSSProperties = {
        border: '1px solid #E1E4E6',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 14,
        outline: 'none',
    };

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
        setPage(1);
    };

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: colors.white,
                marginTop: 64,
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Col className="w-full px-[64px] pt-[32px] pb-[16px]">
                <Row style={{ alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <span
                        style={{ fontSize: 14, color: '#3B82F6', cursor: 'pointer' }}
                        onClick={() => navigate('/checkin')}
                    >
                        ← 오늘 출석
                    </span>
                    <Subtitle1Bold>출석 이력</Subtitle1Bold>
                </Row>

                {/* 필터 영역 */}
                <Col style={{ gap: 12 }}>
                    {/* 날짜 필터 */}
                    <Row style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {datePresets.map((preset) => {
                            const sel = startDate === preset.start && endDate === preset.end;
                            return (
                                <button
                                    key={preset.label}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: 16,
                                        fontSize: 12,
                                        fontWeight: sel ? 600 : 400,
                                        backgroundColor: sel ? '#3B82F6' : '#F3F4F6',
                                        color: sel ? '#fff' : '#4B5563',
                                        border: sel ? '1px solid #3B82F6' : '1px solid #D1D5DB',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleDateChange(preset.start, preset.end)}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                        <span style={{ fontSize: 12, color: '#D1D5DB', margin: '0 4px' }}>|</span>
                        <input
                            style={inputStyle}
                            type="date"
                            value={startDate}
                            onChange={(e) => handleDateChange(e.target.value, endDate)}
                        />
                        <span style={{ fontSize: 14, color: '#6B7280' }}>~</span>
                        <input
                            style={inputStyle}
                            type="date"
                            value={endDate}
                            onChange={(e) => handleDateChange(startDate, e.target.value)}
                        />
                    </Row>

                    {/* 검색 + 정보 */}
                    <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Row style={{ gap: 8, alignItems: 'center' }}>
                            <input
                                style={{
                                    ...inputStyle,
                                    width: 280,
                                }}
                                placeholder="회원명 또는 연락처 검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button
                                    style={{
                                        fontSize: 12,
                                        color: '#9CA3AF',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => { setSearchInput(''); setDebouncedSearch(''); setPage(1); }}
                                >
                                    ✕ 초기화
                                </button>
                            )}
                        </Row>
                        <Row style={{ gap: 12, alignItems: 'center' }}>
                            <Body3Regular style={{ color: colors.LAVEL_1 }}>
                                총 {totalResults.toLocaleString()}건
                            </Body3Regular>
                            {totalPages > 1 && (
                                <CaptionLight style={{ color: colors.LAVEL_1 }}>
                                    ({page} / {totalPages} 페이지)
                                </CaptionLight>
                            )}
                        </Row>
                    </Row>
                </Col>
            </Col>

            {/* 데이터 그리드 */}
            <Col style={{ width: 'calc(100% - 128px)' }}>
                <Grid
                    colDefs={colDefs}
                    rowData={checkins}
                    isInfinity={true}
                />
            </Col>

            {/* 서버 사이드 페이지네이션 */}
            {totalPages > 1 && (
                <Row style={{ gap: 4, alignItems: 'center', padding: '16px 0 32px', justifyContent: 'center' }}>
                    <button
                        style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            border: '1px solid #D1D5DB',
                            backgroundColor: '#fff',
                            color: page <= 1 ? '#D1D5DB' : '#374151',
                            cursor: page <= 1 ? 'default' : 'pointer',
                        }}
                        disabled={page <= 1}
                        onClick={() => setPage(1)}
                    >
                        ≪
                    </button>
                    <button
                        style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            border: '1px solid #D1D5DB',
                            backgroundColor: '#fff',
                            color: page <= 1 ? '#D1D5DB' : '#374151',
                            cursor: page <= 1 ? 'default' : 'pointer',
                        }}
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        ‹ 이전
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                            pageNum = i + 1;
                        } else if (page <= 3) {
                            pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                        } else {
                            pageNum = page - 2 + i;
                        }
                        const sel = pageNum === page;
                        return (
                            <button
                                key={pageNum}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: sel ? 700 : 400,
                                    backgroundColor: sel ? '#3B82F6' : '#fff',
                                    color: sel ? '#fff' : '#374151',
                                    border: sel ? '1px solid #3B82F6' : '1px solid #D1D5DB',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setPage(pageNum)}
                            >
                                {pageNum}
                            </button>
                        );
                    })}

                    <button
                        style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            border: '1px solid #D1D5DB',
                            backgroundColor: '#fff',
                            color: page >= totalPages ? '#D1D5DB' : '#374151',
                            cursor: page >= totalPages ? 'default' : 'pointer',
                        }}
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                        다음 ›
                    </button>
                    <button
                        style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            border: '1px solid #D1D5DB',
                            backgroundColor: '#fff',
                            color: page >= totalPages ? '#D1D5DB' : '#374151',
                            cursor: page >= totalPages ? 'default' : 'pointer',
                        }}
                        disabled={page >= totalPages}
                        onClick={() => setPage(totalPages)}
                    >
                        ≫
                    </button>
                </Row>
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default CheckinHistoryPage;
